import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowRight, FileText, Loader2, Mail, ExternalLink, ChevronRight, Lock } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com";

const industryLabel = (s = "") =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ── States: "form" | "sent" | "reports" | "error" ────────────────────────────

const MyReportsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [view, setView] = useState("form");       // form | sent | reports | error
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  // On mount: if ?token= present, auto-verify
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) verifyToken(token);
  }, []); // eslint-disable-line

  const verifyToken = async (token) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/verify?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.detail || "This link has expired or is invalid.");
        setView("error");
        return;
      }
      const data = await res.json();
      setReports(data.reports || []);
      setView("reports");
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
      setView("error");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/request-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error();
      setView("sent");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Loading spinner (token verification in progress) ─────────────────────
  if (loading && view === "form") {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3b82f6] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <Helmet>
        <title>My Reports — AI Workshop LLC</title>
        <meta name="description" content="Access your purchased AI implementation blueprints." />
      </Helmet>
      <Header />

      <main className="pt-32 pb-24 px-4">
        <div className="max-w-lg mx-auto">

          {/* ── Email form ──────────────────────────────────────────────── */}
          {view === "form" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="text-center mb-10">
                <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-5">
                  <FileText className="w-8 h-8 text-[#3b82f6]" />
                </div>
                <h1 className="text-3xl font-black mb-3">Access My Blueprints</h1>
                <p className="text-slate-400 text-base leading-relaxed">
                  Enter the email you used at checkout. We'll send you a secure
                  link to view all your purchased blueprints — no password needed.
                </p>
              </div>

              <form onSubmit={handleRequestAccess} className="space-y-4">
                <div>
                  <label htmlFor="email" className="sr-only">Email address</label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-slate-950/50 border-white/15 text-white placeholder:text-slate-500 h-12 text-base rounded-xl"
                    required
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:shadow-blue-500/30 text-slate-950 font-black h-12 rounded-full text-base hover:scale-[1.02] transition-all"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Send My Access Link
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>

              <div className="flex items-center gap-2 mt-5 text-xs text-slate-600 justify-center">
                <Lock className="w-3 h-3" />
                <span>Link expires in 24 hours · no account required</span>
              </div>

              <p className="text-center text-slate-500 text-sm mt-8">
                Don't have a blueprint yet?{" "}
                <button
                  onClick={() => navigate("/audit")}
                  className="text-[#3b82f6] hover:text-blue-300 font-semibold transition-colors"
                >
                  Get yours for $100
                </button>
              </p>
            </motion.div>
          )}

          {/* ── Email sent confirmation ──────────────────────────────────── */}
          {view === "sent" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-5">
                <Mail className="w-8 h-8 text-blue-400" />
              </div>
              <h2 className="text-2xl font-black mb-3">Check your inbox</h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                We sent a secure access link to <strong className="text-white">{email}</strong>.
                Click the link in that email to view your blueprints.
              </p>
              <p className="text-slate-500 text-sm mb-6">
                Didn't receive it? Check your spam folder, or{" "}
                <button
                  onClick={() => setView("form")}
                  className="text-[#3b82f6] hover:text-blue-300 font-semibold"
                >
                  try again
                </button>
                .
              </p>
              <p className="text-slate-600 text-xs">Link expires in 24 hours.</p>
            </motion.div>
          )}

          {/* ── Reports list ────────────────────────────────────────────── */}
          {view === "reports" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-2xl font-black mb-2">Your Blueprints</h1>
                <p className="text-slate-400 text-sm">
                  {reports.length === 1
                    ? "1 blueprint found"
                    : `${reports.length} blueprints found`}
                </p>
              </div>

              <div className="space-y-3">
                {reports.map((r) => (
                  <motion.div
                    key={r.reportId}
                    whileHover={{ y: -2 }}
                    className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-5 hover:border-blue-500/30 transition-all cursor-pointer"
                    onClick={() => navigate(r.isPaid ? `/full-report/${r.reportId}` : `/report-preview/${r.reportId}`)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-[#3b82f6]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-white text-sm leading-snug truncate">
                            {r.businessName || "Your Business"}
                          </p>
                          <p className="text-slate-500 text-xs mt-0.5">
                            {industryLabel(r.industry)}
                            {r.createdAt
                              ? ` · ${new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                              : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-[10px] font-semibold border px-2.5 py-1 rounded-full ${
                          r.isPaid
                            ? "bg-blue-500/10 text-[#3b82f6] border-blue-500/20"
                            : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                        }`}>
                          {r.isPaid ? "Full Access" : "Preview"}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-white/8 flex items-center justify-between text-sm">
                <button
                  onClick={() => { setView("form"); setEmail(""); }}
                  className="text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Use a different email
                </button>
                <button
                  onClick={() => navigate("/audit")}
                  className="text-[#3b82f6] hover:text-blue-300 font-semibold flex items-center gap-1 transition-colors"
                >
                  Get another blueprint
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ── Error state ─────────────────────────────────────────────── */}
          {view === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-5">
                <span className="text-2xl">⚠️</span>
              </div>
              <h2 className="text-2xl font-black mb-3">Link expired</h2>
              <p className="text-slate-400 leading-relaxed mb-6">{errorMsg}</p>
              <Button
                onClick={() => setView("form")}
                className="bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold px-8 py-3 rounded-full hover:scale-[1.02] transition-all"
              >
                Request a new link
              </Button>
            </motion.div>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MyReportsPage;
